import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ModifierGroup, ModifierGroupsService } from '../modifier-groups.service';
import { ModifierGroupFormDialogComponent } from '../modifier-group-form-dialog/modifier-group-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-modifiers-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading modifiers...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>tune</mat-icon></div>
          <h3>No modifier groups yet</h3>
          <p>Create groups like Size (Small/Medium/Large) or Extras (Extra Cheese, No Onion).</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'modifier.create'">
            <mat-icon>add</mat-icon> Add Modifier Group
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="table-container">
          <table mat-table [dataSource]="items" class="modern-table">
            <ng-container matColumnDef="name">
              <th mat-header-cell *matHeaderCellDef>Group</th>
              <td mat-cell *matCellDef="let g">
                <div class="entity-cell">
                  <div class="entity-cell__icon"><mat-icon>tune</mat-icon></div>
                  <div class="entity-cell__details">
                    <strong class="entity-cell__name">{{ g.name }}</strong>
                    <span class="entity-cell__sub">{{ optionNames(g) }}</span>
                  </div>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="type">
              <th mat-header-cell *matHeaderCellDef>Selection</th>
              <td mat-cell *matCellDef="let g"><span class="pill">{{ g.selection_type }}</span></td>
            </ng-container>

            <ng-container matColumnDef="required">
              <th mat-header-cell *matHeaderCellDef>Required</th>
              <td mat-cell *matCellDef="let g">{{ g.is_required ? 'Yes' : 'No' }}</td>
            </ng-container>

            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td mat-cell *matCellDef="let g">
                <span class="status-badge" [class.active]="g.is_active" [class.inactive]="!g.is_active">
                  <span class="status-indicator"></span>{{ g.is_active ? 'Active' : 'Inactive' }}
                </span>
              </td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef>Actions</th>
              <td mat-cell *matCellDef="let g">
                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions" class="action-menu-trigger">
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu" class="action-menu">
                  <button mat-menu-item (click)="onEdit(g)" *appHasPermission="'modifier.update'">
                    <mat-icon>edit</mat-icon><span>Edit</span>
                  </button>
                  <button mat-menu-item (click)="onToggle(g)" *appHasPermission="'modifier.update'">
                    <mat-icon>{{ g.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                    <span>{{ g.is_active ? 'Deactivate' : 'Activate' }}</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button mat-menu-item (click)="onDelete(g)" class="danger-action" *appHasPermission="'modifier.delete'">
                    <mat-icon>delete</mat-icon><span>Delete</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>

          <mat-paginator
            [length]="total"
            [pageSize]="pageSize"
            [pageIndex]="pageIndex"
            [pageSizeOptions]="pageSizeOptions"
            [showFirstLastButtons]="true"
            aria-label="Modifier group pagination"
            (page)="handlePageEvent($event)"
          ></mat-paginator>
        </div>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
})
export class ModifiersListComponent implements OnInit, OnDestroy {
  items: ModifierGroup[] = [];
  loading = false;
  displayedColumns = ['name', 'type', 'required', 'status', 'actions'];

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: ModifierGroupsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Modifiers',
      icon: 'tune',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'modifier.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Modifier Group', icon: 'add', variant: 'primary', permission: 'modifier.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  optionNames(g: ModifierGroup): string {
    return (g.options ?? []).map((o) => o.name).join(', ') || 'No options';
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load modifiers', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(ModifierGroupFormDialogComponent, { width: '640px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(g: ModifierGroup): void {
    this.dialog.open(ModifierGroupFormDialogComponent, { width: '640px', maxHeight: '90vh', data: { id: g.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(g: ModifierGroup): void {
    this.service.toggleActive(g.id).subscribe({
      next: (res) => {
        if (res.success) {
          g.is_active = !g.is_active;
          this.snackBar.open(`Modifier group ${g.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(g: ModifierGroup): void {
    this.confirmationService.confirm({
      title: 'Delete modifier group',
      message: `Are you sure you want to delete "${g.name}"? This also removes its options.`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(g.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Modifier group deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
